import { Fragment, useId, useRef, useState, useSyncExternalStore } from "react";
import type { KeyboardEvent } from "react";
import classNames from "classnames";

import Markdown from "components/markdown/markdown";
import IconTabArrow from "../../assets/images/svg/icon-tab-arrow.svg";

interface TabProp {
  id: number;
  Title: string;
  Content: string;
}

interface TabsProps {
  data: {
    Tab: TabProp[];
  };
}

const mobileQuery = "(max-width: 767px), screen and (max-width: 812px) and (orientation: landscape)";
const getMobileSnapshot = () => window.matchMedia(mobileQuery).matches;
const getServerSnapshot = () => false;
const subscribeToMobile = (onChange: () => void) => {
  const query = window.matchMedia(mobileQuery);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
};

const Tabs: React.FC<TabsProps> = ({ data }) => {
  const id = useId();
  const [activeTab, setActiveTab] = useState<number | null>(0);
  const tabButtons = useRef<Array<HTMLButtonElement | null>>([]);
  const isMobile = useSyncExternalStore(subscribeToMobile, getMobileSnapshot, getServerSnapshot);
  const tabs = data.Tab ?? [];
  const selectedTab = isMobile ? activeTab : (activeTab ?? 0);

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number;

    switch (event.key) {
      case "ArrowDown":
        nextIndex = (index + 1) % tabs.length;
        break;
      case "ArrowUp":
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    setActiveTab(nextIndex);
    tabButtons.current[nextIndex]?.focus();
  };

  if (!tabs.length) return null;

  return (
    <section className="tabs">
      <div className="container">
        <div className="tabs__inner">
          <div className="tabs__side">
            <div
              role="tablist"
              aria-orientation="vertical"
              aria-label={tabs.map(item => item.Title).join(" / ")}
            >
              {tabs.map((item, index) => (
                <h2 key={item.id ?? index}>
                  <button
                    type="button"
                    role="tab"
                    id={`${id}-tab-${index}`}
                    aria-controls={`${id}-panel-${index}`}
                    aria-selected={selectedTab === index}
                    tabIndex={selectedTab === index ? 0 : -1}
                    ref={node => { tabButtons.current[index] = node; }}
                    onClick={() => setActiveTab(index)}
                    onKeyDown={event => handleTabKeyDown(event, index)}
                    className={classNames("tab-button", { "is-active": selectedTab === index })}
                  >
                    {item.Title}
                    <span className="tab-button__icon" aria-hidden="true">
                      <IconTabArrow />
                    </span>
                  </button>
                </h2>
              ))}
            </div>
          </div>
          <div className="tabs__content">
            {tabs.map((item, index) => (
              <Fragment key={item.id ?? index}>
                <h2 className="tabs__content-title">
                  <button
                    type="button"
                    id={`${id}-accordion-${index}`}
                    aria-controls={`${id}-panel-${index}`}
                    aria-expanded={selectedTab === index}
                    onClick={() => setActiveTab(activeTab === index ? null : index)}
                    className={classNames("tab-button", { "is-active": selectedTab === index })}
                  >
                    {item.Title}
                    <span className="tab-button__icon" aria-hidden="true">
                      <IconTabArrow />
                    </span>
                  </button>
                </h2>
                <div
                  id={`${id}-panel-${index}`}
                  className="tab-content"
                  role={isMobile ? "region" : "tabpanel"}
                  aria-labelledby={`${id}-${isMobile ? "accordion" : "tab"}-${index}`}
                  tabIndex={isMobile ? undefined : 0}
                  hidden={selectedTab !== index}
                >
                  <div className="tab-content__inner">
                    <Markdown content={item.Content} />
                  </div>
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Tabs;
